/**
 * @module components/analysis/GenreFitLearnMore
 * @description
 *   Inline "View" panel under Trend alignment (Hit Potential card).
 *
 *   What a skimmer should know:
 *   - Explains *methodology + axis %* — not the Trend insights advice tips.
 *   - Desktop (lg+): CSS grid — copy left (method/neighbors + axes), radar right.
 *   - Compact (<lg): flex column DOM order — method + neighbors → radar → Axis fit.
 *     (No display:contents — it can collapse Recharts height in Chrome mobile.)
 *   - Compact + focused axis: definition shown inline under the progress bar;
 *     no LearnMoreTip nested button (row is a single tap target).
 *   - Desktop axis rows keep LearnMoreTip hover copy.
 *   - When a Trend insight tip is tapped, HitPotentialSection passes focusAxisId;
 *     that Axis fit row is highlighted and smooth-scrolled to the viewport center
 *     once the View panel has opened (panelReady).
 *   - Click another Axis fit row to switch focus without scrolling; click away from
 *     axis rows to clear (radar spoke emphasize clears with focusAxisId).
 *   - Radar emphasizes the same spoke.
 *   - Sonic neighbor chips use LearnMoreTip (hover desktop / tap compact).
 *
 *   Data comes from trendAnalysis.trendFitBreakdown (pack method copy, axes,
 *   nearestGenres, nearestGenresMode, selectedPackId). When a subgenre is
 *   selected, selectedPackId is the child pack actually scored.
 *   Method copy is the shared honesty line only (no “Scored against …” suffix);
 *   radar shows Parent · Sub when the child pack is scored.
 *
 *   Sonic neighbors: same_parent mode stays under the selected genre; mixed
 *   mode fills thin trees with cross-parent lanes.
 */

import React, { useEffect, useRef } from 'react';
import { Minimize2 } from 'lucide-react';
import { Card, Chip, IconTile, Progress } from '../ui';
import GenreFitRadar from './GenreFitRadar';
import LearnMoreTip from './LearnMoreTip';
import { getAxisFitDefinition, getSonicNeighborReason } from './genreFitDefinitions';
import useMediaQuery, { COMPACT_MAX_LG } from '../../hooks/useMediaQuery';

/** Default method line when older sessions omit methodSummary. */
export const FALLBACK_METHOD_SUMMARY =
  'Built from curated genre characteristics and maintained with global music data';

/**
 * @param {object} props
 * @param {object|null} [props.breakdown] trendFitBreakdown from the API
 * @param {string} [props.selectedGenreLabel] fallback label if breakdown omits it
 * @param {string|null} [props.focusAxisId] axis to highlight (from tip jump / row click)
 * @param {(axisId: string|null) => void} [props.onFocusAxisChange] set/clear axis focus
 * @param {boolean} [props.panelReady=true] parent View expand finished enough to scroll
 * @param {() => void} props.onClose
 * @param {string} [props.className]
 */
export default function GenreFitLearnMore({
  breakdown,
  selectedGenreLabel,
  focusAxisId = null,
  onFocusAxisChange,
  panelReady = true,
  onClose,
  className = '',
}) {
  // --- Unpack breakdown with safe defaults ---------------------------------
  // Prefer selectedPackId from the API. Radar: child pack → Parent · Sub.
  const methodTitle = breakdown?.methodTitle || 'Genre profiles';
  const methodSummary = breakdown?.methodSummary || FALLBACK_METHOD_SUMMARY;
  const genreLabel =
    breakdown?.selectedGenreLabel || selectedGenreLabel || 'selected genre';
  const subgenreLabel = breakdown?.selectedSubgenreLabel || null;
  const selectedGenreId = breakdown?.selectedGenre || null;
  const selectedPackId = breakdown?.selectedPackId || selectedGenreId;
  const usesOwnCenterProfile =
    Boolean(subgenreLabel) &&
    Boolean(breakdown?.selectedSubgenre) &&
    selectedPackId === breakdown.selectedSubgenre;
  // Radar: child pack → Parent · Sub; parent-only → parent label.
  const radarGenreLabel = usesOwnCenterProfile
    ? `${genreLabel} · ${subgenreLabel}`
    : genreLabel;
  const methodLine = /\.\s*$/.test(String(methodSummary).trim())
    ? String(methodSummary).trim()
    : `${String(methodSummary).trim()}.`;
  const axes = Array.isArray(breakdown?.axes) ? breakdown.axes : [];
  // Never show instrumental or the user's own selected genre as a "neighbor".
  const nearest = (Array.isArray(breakdown?.nearestGenres) ? breakdown.nearestGenres : []).filter(
    (g) => g?.id !== 'instrumental' && g?.id !== breakdown?.selectedGenre
  );
  const nearestMode = breakdown?.nearestGenresMode === 'same_parent' ? 'same_parent' : 'mixed';
  const nearestCaption =
    nearestMode === 'same_parent'
      ? 'Closest sibling lanes under your genre by sonic fit.'
      : 'Closest other lanes by sonic fit.';
  const focusRowRef = useRef(null);
  // Row clicks set focus without scroll; tip jumps leave this false so we center.
  const skipNextFocusScrollRef = useRef(false);
  const isCompact = useMediaQuery(COMPACT_MAX_LG);

  // Center the focused Axis fit row only for tip jumps (not Axis fit row clicks).
  // Wait until the parent View panel has opened so layout height is real.
  useEffect(() => {
    if (!focusAxisId || !panelReady || !focusRowRef.current) return undefined;
    if (skipNextFocusScrollRef.current) {
      skipNextFocusScrollRef.current = false;
      return undefined;
    }
    if (typeof focusRowRef.current.scrollIntoView !== 'function') return undefined;

    const row = focusRowRef.current;
    // Short delay so expand animation / highlight paint apply before smooth scroll.
    const timeoutId = window.setTimeout(() => {
      row.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [focusAxisId, panelReady]);

  // --- Axis focus clear on click-away --------------------------------------
  // Tip jumps set focus via parent; row clicks switch focus. Any pointerdown
  // outside an Axis fit row (and outside trend-move tips) clears highlight.
  useEffect(() => {
    if (!focusAxisId || typeof onFocusAxisChange !== 'function') return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-axis-row]')) return;
      if (target.closest('[data-trend-move-tip]')) return;
      onFocusAxisChange(null);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [focusAxisId, onFocusAxisChange]);

  const focusAxisFromRow = (axisId) => {
    skipNextFocusScrollRef.current = true;
    onFocusAxisChange?.(axisId);
  };

  return (
    <Card
      variant="inset"
      className={`p-4 sm:p-6 relative overflow-hidden ${className}`.trim()}
      role="region"
      aria-label="Genre fit View"
    >
      {/* Close control (shared with Technical View chrome) */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 z-10 inline-flex items-center justify-center rounded-xl border border-white/[0.08] bg-black/30 p-2 text-critic-text-secondary hover:text-critic-text-primary hover:border-white/[0.16] transition-colors"
        aria-label="Close genre fit details"
      >
        <Minimize2 className="h-4 w-4" />
      </button>

      {/*
        Compact (flex-col): method+neighbors → radar → Axis fit (DOM order).
        Desktop (lg grid): copy left (A then B) · radar right spanning both rows.
        Avoid display:contents — it can collapse Recharts height in Chrome mobile.
      */}
      <div className="pr-10 flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-stretch">
        {/* Block A: method title + honesty blurb + sonic neighbors */}
        <div className="min-w-0 space-y-5 lg:col-start-1 lg:row-start-1">
          <div className="flex items-center space-x-3 min-w-0">
            <IconTile size="sm" gradient="insight">
              <span className="text-white font-bold text-sm">G</span>
            </IconTile>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-critic-text-muted">
                View
              </p>
              <h4 className="font-display text-lg sm:text-xl font-semibold text-critic-text-primary">
                {methodTitle}
              </h4>
            </div>
          </div>

          <p className="text-sm md:text-[0.95rem] leading-relaxed text-critic-text-primary">
            {methodLine}
          </p>

          {nearest.length > 0 ? (
            <div className="space-y-2">
              <div>
                <h5 className="font-display text-sm font-semibold text-critic-text-primary">
                  Sonic neighbors
                </h5>
                <p className="text-xs text-critic-text-muted mt-0.5">
                  {nearestCaption}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {nearest.map((g) => {
                  const reason = getSonicNeighborReason(g);
                  const chipLabel = `${g.label}${
                    typeof g.fit === 'number' ? ` · ${Math.round(g.fit * 100)}%` : ''
                  }`;
                  return (
                    <LearnMoreTip
                      key={g.id || g.label}
                      id={`sonic-neighbor-${g.id || g.label}`}
                      label={g.label || 'Neighbor'}
                      definition={reason}
                      cursor="default"
                    >
                      <Chip className="text-xs sm:text-sm cursor-default">{chipLabel}</Chip>
                    </LearnMoreTip>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        {/* Block D: radar — after method on compact; right column on lg */}
        <div
          className="min-w-0 w-full shrink-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-stretch flex flex-col"
          data-genre-fit-radar
        >
          <GenreFitRadar
            axes={axes}
            genreLabel={radarGenreLabel}
            focusAxisId={focusAxisId}
            className="h-72 sm:h-80 w-full shrink-0 lg:h-full lg:min-h-[22rem] lg:flex-1 lg:shrink"
          />
        </div>

        {/* Block B: per-axis fit % — tip jumps land here via focusAxisId */}
        {axes.length > 0 ? (
          <div
            className="min-w-0 space-y-3 lg:col-start-1 lg:row-start-2"
            data-genre-fit-axes
          >
            <h5 className="font-display text-sm font-semibold text-critic-text-primary">
              Axis fit
            </h5>
            <ul className="space-y-3">
              {axes.map((axis) => {
                const fitPct = Math.round(clamp01(axis.fit) * 100);
                const axisId = axis.id || axis.label;
                const focused = Boolean(focusAxisId) && axisId === focusAxisId;
                const axisDefinition = getAxisFitDefinition(axis.id);
                return (
                  <li
                    key={axisId}
                    id={`genre-fit-axis-${axisId}`}
                    ref={focused ? focusRowRef : undefined}
                    data-axis-row
                    data-axis-id={axisId}
                    data-focused={focused ? 'true' : undefined}
                    aria-current={focused ? 'true' : undefined}
                    className={[
                      'space-y-1.5 rounded-xl px-2.5 py-2 -mx-2.5 transition-colors cursor-pointer',
                      focused
                        ? 'bg-apple-blue/15 ring-1 ring-apple-blue/40'
                        : 'hover:bg-white/[0.04]',
                    ]
                      .join(' ')
                      .trim()}
                    onClick={() => focusAxisFromRow(axisId)}
                  >
                    <div className="flex items-center justify-between gap-3 text-sm">
                      {isCompact ? (
                        <span
                          className={`text-sm truncate ${
                            focused
                              ? 'text-critic-text-primary font-medium'
                              : 'text-critic-text-secondary'
                          }`}
                        >
                          {axis.label || axis.id}
                        </span>
                      ) : (
                        <LearnMoreTip
                          id={`axis-${axisId}`}
                          label={axis.label || axis.id}
                          definition={axisDefinition}
                          cursor="pointer"
                          labelClassName={`text-sm ${
                            focused
                              ? 'text-critic-text-primary font-medium'
                              : 'text-critic-text-secondary'
                          }`}
                        />
                      )}
                      <span className="tabular-nums text-critic-text-primary shrink-0">
                        {fitPct}%
                      </span>
                    </div>
                    <Progress
                      value={fitPct}
                      aria-label={`${axis.label || axis.id} fit ${fitPct}%`}
                      barClassName={focused ? 'bg-apple-blue' : 'bg-apple-blue/80'}
                    />
                    {/* Compact: definition under bar when this row is focused */}
                    {isCompact && focused && axisDefinition ? (
                      <p
                        className="text-xs leading-relaxed text-critic-text-muted pt-0.5"
                        data-axis-inline-def={axisId}
                      >
                        {axisDefinition}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
